import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Input, Button, Tag, message, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';

import { Slot, PaginationInfo } from '../../../Modules/Slot/Model/Slot';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { getAccountIdById } from '../../../Utils/getAccountNameById';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { isSlotOperationRestricted } from '../../../Utils/timeRestriction';

interface Props {
  data: Slot[];
  loading: boolean;
  selected: Key[];
  onSelectChange: (ids: Key[]) => void;
  onInlineUpdate: (id: number, dto: Partial<Slot>) => Promise<void>;
  pagination?: PaginationInfo;
  onPageChange?: (page: number, pageSize?: number) => void;
}

/* 상태별 태그 색 */
const colorMap: Record<Slot['status'], string> = {
  정상: 'green',
  연장: 'green',
  환불: 'red',
  종료: 'orange',
};

const SlotTable: React.FC<Props> = ({
  data,
  loading,
  selected,
  onSelectChange,
  onInlineUpdate,
  pagination,
  onPageChange,
}) => {
  const accounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);

  // 모바일 판별 (<=489px)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 시간 제한 확인
  const isRestricted = currentUser ? isSlotOperationRestricted(currentUser) : false;

  const sellers = useMemo(() => 
    accounts
      .filter(a => a.grade === 'SELLER')
      .sort((a, b) => a.username.localeCompare(b.username)),
    [accounts]
  );

  /* URL 검사 */
  const isValidUrl = useCallback((url?: string) => {
    if (!url) return false;
    try { new URL(url); return true; } catch { return false; }
  }, []);

  /* ───── 컬럼 정의 ───── */
  const columns: ColumnsType<Slot> = useMemo(() => [
    { title: '슬롯 번호', dataIndex: 'slotId', width: 85, align: 'center' },

    {
      title: '상태',
      dataIndex: 'status',
      width: 70,
      render: (v: Slot['status']) => {
        const displayText = v === '연장' ? '정상' : v;
        return <Tag color={colorMap[v]}>{displayText}</Tag>;
      },
    },
    {
      title: '아이디',
      dataIndex: 'user',
      width: 120,
      align: 'center',
      render: (id: number) => getAccountIdById(accounts, id, false, currentUser) ?? '-',
    },
    { title: '시작일', dataIndex: 'start_date', width: 120 },
    { title: '종료일', dataIndex: 'end_date', width: 120 },

    /* ───── 읽기 전용 필드들 ───── */
    {
      title: '메인키워드',
      dataIndex: 'main_keyword',
      render: (v) => v,
    },
    {
      title: '단일 MID',
      dataIndex: 'mid_single',
      width: 120,
      render: (v) => v,
    },
    {
      title: '가격비교 MID',
      dataIndex: 'mid_compare',
      width: 120,
      render: (v) => v,
    },
    {
      title: '상품 URL',
      dataIndex: 'product_url',
      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">바로가기</a> : '-',
    },
    {
      title: '가격비교 URL',
      dataIndex: 'compare_url',
      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">바로가기</a> : '-',
    },
    {
      title: '메모',
      dataIndex: 'memo',
      render: (v) => v,
    },
  ], [accounts, currentUser]);

  // 테이블 변경 핸들러
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      // 페이지 변경만 처리
      if (pagination.current !== undefined) {
        onPageChange?.(pagination.current, pagination.pageSize || 20);
      }
    },
    [onPageChange],
  );

  // 페이지네이션 설정
  const tablePagination = pagination ? {
    current: pagination.current_page || 1,
    pageSize: pagination.page_size || 20,
    total: pagination.total_count || 0,
    position: ['bottomCenter' as const],
    showSizeChanger: false, // 페이지 크기 변경 숨김
    showQuickJumper: false,
    showTotal: (total: number, range: [number, number]) => 
      `${range[0]}-${range[1]} / ${total.toLocaleString()}개 슬롯`,
    showLessItems: true,
    responsive: true,
  } : false;

  return (
    <Table
      rowKey="slotId"
      dataSource={data}
      columns={columns}
      loading={loading}
      rowSelection={{
        type: 'checkbox',
        selectedRowKeys: selected,
        onChange: onSelectChange,
        // 모든 슬롯을 선택할 수 있도록 제한 제거
      }}
      style={isMobile ? { width: '100%' } : { minWidth: 950 }}
      scroll={{ x: 'max-content' }}
      pagination={tablePagination}
      onChange={handleTableChange}
    />
  );
};

export default React.memo(SlotTable); 